/**
 * Address autocomplete using Google Places API
 */

export interface AddressSuggestion {
  display_name: string;
  place_id: string | number;
  lat: string;
  lon: string;
  /** Business or place name (Google structured_formatting.main_text). */
  main_text?: string;
  secondary_text?: string;
  address?: {
    house_number?: string;
    road?: string;
    street?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    region?: string;
    postcode?: string;
    zipCode?: string;
    country?: string;
    country_code?: string;
  };
}

// Check if Google Places API is available
const isGooglePlacesAvailable = (): boolean => {
  return typeof window !== 'undefined' &&
         typeof (window as any).google !== 'undefined' &&
         typeof (window as any).google.maps !== 'undefined' &&
         typeof (window as any).google.maps.places !== 'undefined';
};

// Load Google Maps API script dynamically
let googleMapsLoading = false;
let googleMapsLoaded = false;

const loadGoogleMapsAPI = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGooglePlacesAvailable()) {
      googleMapsLoaded = true;
      resolve();
      return;
    }

    if (googleMapsLoading) {
      // Wait for existing load
      const checkInterval = setInterval(() => {
        if (isGooglePlacesAvailable()) {
          clearInterval(checkInterval);
          googleMapsLoaded = true;
          resolve();
        }
      }, 100);
      return;
    }

    googleMapsLoading = true;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

    if (!apiKey) {
      reject(new Error('Google Maps API key not configured'));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      googleMapsLoaded = true;
      resolve();
    };

    script.onerror = () => {
      googleMapsLoading = false;
      reject(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(script);

    // Timeout after 10 seconds
    setTimeout(() => {
      if (!googleMapsLoaded) {
        googleMapsLoading = false;
        reject(new Error('Google Maps API load timeout'));
      }
    }, 10000);
  });
};

// Wait for Google Maps API to be ready
const waitForGoogleMaps = (timeout: number = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (isGooglePlacesAvailable()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isGooglePlacesAvailable()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
};

export type PlaceSearchMode = 'address' | 'places';

/**
 * Main address search function using Google Places API.
 * Use mode `places` for businesses + addresses (trip builder); `address` for street-focused search.
 */
export const searchAddress = async (
  query: string,
  limit: number = 10,
  mode: PlaceSearchMode = 'address',
): Promise<AddressSuggestion[]> => {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return [];
  }

  // Check if Google Maps API is already loaded (from MapView component)
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

  if (apiKey) {
    try {
      // Wait for Google Maps API to be available (it should already be loaded by MapView)
      const isAvailable = await waitForGoogleMaps(3000);

      if (isAvailable && isGooglePlacesAvailable()) {
        return await searchGooglePlaces(trimmedQuery, limit, mode);
      } else {
        await loadGoogleMapsAPI();

        if (isGooglePlacesAvailable()) {
          return await searchGooglePlaces(trimmedQuery, limit, mode);
        } else {
          console.error('Google Places API could not be loaded');
          return [];
        }
      }
    } catch (error) {
      console.error('Google Places API failed:', error);
      return [];
    }
  }

  console.warn('No Google Maps API key available for address autocomplete');
  return [];
};

/** Load Google Maps JS if needed (Places, Distance Matrix). */
export async function ensureGoogleMapsLoaded(): Promise<boolean> {
  const apiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  if (!apiKey) return false;
  try {
    if (await waitForGoogleMaps(3000)) return true;
    await loadGoogleMapsAPI();
    return isGooglePlacesAvailable();
  } catch {
    return false;
  }
}

/** Alias for business-friendly place search. */
export const searchPlaces = (query: string, limit: number = 10) =>
  searchAddress(query, limit, 'places');

function getPlacePredictions(
  service: google.maps.places.AutocompleteService,
  input: string,
  types?: string[],
): Promise<google.maps.places.AutocompletePrediction[]> {
  return new Promise((resolve, reject) => {
    const g = (window as typeof window & { google: typeof google }).google;
    const request: google.maps.places.AutocompletionRequest = {
      input,
      componentRestrictions: { country: 'us' },
    };
    if (types && types.length > 0) {
      request.types = types;
    }

    service.getPlacePredictions(request, (predictions, status) => {
      if (status === g.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
        resolve(predictions);
        return;
      }
      if (status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        resolve([]);
        return;
      }
      reject(new Error(`Google Places API error: ${status}`));
    });
  });
}

/**
 * Search using Google Places Autocomplete API
 */
const searchGooglePlaces = async (
  query: string,
  limit: number = 10,
  mode: PlaceSearchMode = 'address',
): Promise<AddressSuggestion[]> => {
  if (!isGooglePlacesAvailable()) {
    throw new Error('Google Places API not available');
  }

  const service = new (window as any).google.maps.places.AutocompleteService();

  const isStateOnly =
    /^[A-Z]{2}$/.test(query.trim()) ||
    (/^[A-Z][a-z]+(\s+[A-Z][a-z]+)*$/.test(query.trim()) && query.split(',').length === 1);
  const isCityState = /,/.test(query) && !/\d/.test(query) && query.split(',').length === 2;
  const isCityStateNoComma =
    !/,/.test(query) &&
    !/\d/.test(query) &&
    query.split(/\s+/).length >= 2 &&
    query.split(/\s+/).length <= 4;
  const isCityStateOrState = isStateOnly || isCityState || isCityStateNoComma;

  let predictions: google.maps.places.AutocompletePrediction[] = [];

  if (isCityStateOrState) {
    predictions = await getPlacePredictions(service, query, ['(cities)']);
  } else if (mode === 'places') {
    const looksLikeStreet = /^\d/.test(query.trim());
    const batches = await Promise.allSettled([
      getPlacePredictions(service, query),
      looksLikeStreet ? Promise.resolve([]) : getPlacePredictions(service, query, ['establishment']),
    ]);
    const merged = new Map<string, google.maps.places.AutocompletePrediction>();
    for (const batch of batches) {
      if (batch.status !== 'fulfilled') continue;
      for (const p of batch.value) {
        if (!merged.has(p.place_id)) merged.set(p.place_id, p);
      }
    }
    predictions = Array.from(merged.values());
  } else {
    predictions = await getPlacePredictions(service, query, ['address']);
  }

  return resolvePredictionDetails(predictions.slice(0, limit));
};

function resolvePredictionDetails(
  predictions: google.maps.places.AutocompletePrediction[],
): Promise<AddressSuggestion[]> {
  return new Promise((resolve) => {
    if (predictions.length === 0) {
      resolve([]);
      return;
    }

    const placesService = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
    const g = (window as any).google;
    const suggestions: AddressSuggestion[] = [];
    let completed = 0;

    for (const prediction of predictions) {
      placesService.getDetails(
        { placeId: prediction.place_id, fields: ['address_components', 'geometry', 'formatted_address', 'name'] },
        (place: google.maps.places.PlaceResult | null, placeStatus: google.maps.places.PlacesServiceStatus) => {
          if (placeStatus === g.maps.places.PlacesServiceStatus.OK && place) {
            const addr: AddressSuggestion['address'] = {};

            if (place.address_components) {
              for (const component of place.address_components) {
                const types = component.types;
                if (types.includes('street_number')) addr.house_number = component.long_name;
                if (types.includes('route')) addr.road = component.long_name;
                if (types.includes('locality')) addr.city = component.long_name;
                if (types.includes('administrative_area_level_2')) addr.county = component.long_name;
                if (types.includes('administrative_area_level_1')) {
                  addr.state = component.short_name;
                  addr.region = component.long_name;
                }
                if (types.includes('postal_code')) {
                  addr.postcode = component.long_name;
                  addr.zipCode = component.long_name;
                }
                if (types.includes('country')) {
                  addr.country = component.long_name;
                  addr.country_code = component.short_name.toLowerCase();
                }
              }
            }

            if (addr.house_number && addr.road) {
              addr.street = `${addr.house_number} ${addr.road}`;
            } else if (addr.road) {
              addr.street = addr.road;
            }

            const lat = place.geometry?.location?.lat()?.toString() || '';
            const lon = place.geometry?.location?.lng()?.toString() || '';

            suggestions.push({
              display_name: place.formatted_address || prediction.description,
              place_id: prediction.place_id,
              lat,
              lon,
              main_text:
                prediction.structured_formatting?.main_text ||
                place.name ||
                prediction.description.split(',')[0],
              secondary_text: prediction.structured_formatting?.secondary_text,
              address: addr && Object.keys(addr).length > 0 ? addr : undefined,
            });
          }

          completed++;
          if (completed === predictions.length) {
            resolve(suggestions);
          }
        },
      );
    }
  });
};